import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, KeyRound, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';

const AdminResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [tokenEmail, setTokenEmail] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      return;
    }

    // Validate token
    const validateToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('verify-reset-token', {
          body: { token },
        });

        if (error || !data?.valid) {
          setIsValidToken(false);
        } else {
          setIsValidToken(true);
          setTokenEmail(data.email || '');
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setIsValidToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('verify-reset-token', {
        body: { token, newPassword: password },
      });

      if (error) throw error;

      if (data?.success) {
        setIsSuccess(true);
        toast.success('Password updated successfully');
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      } else {
        throw new Error(data?.error || 'Failed to update password');
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast.error('Error resetting password', {
        description: error.message || 'Please try again',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while checking token
  if (isValidToken === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Reset Password - Daniel Albert Law"
        description="Reset your admin password"
      />
      <div className="min-h-screen flex flex-col bg-muted/30">
        <div className="p-4">
          <Link 
            to="/admin/login" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                {isSuccess ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : !isValidToken ? (
                  <AlertCircle className="h-6 w-6 text-destructive" />
                ) : (
                  <KeyRound className="h-6 w-6 text-primary" />
                )}
              </div>
              <CardTitle className="text-2xl">
                {isSuccess 
                  ? 'Password Updated' 
                  : !isValidToken 
                    ? 'Invalid Link' 
                    : 'Create New Password'
                }
              </CardTitle>
              <CardDescription>
                {isSuccess 
                  ? 'Your password has been successfully updated'
                  : !isValidToken
                    ? 'This password reset link is invalid or has expired'
                    : 'Enter your new password below'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isValidToken ? (
                <div className="text-center space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Please request a new password reset link.
                  </p>
                  <Button 
                    className="w-full"
                    onClick={() => navigate('/admin/login')}
                  >
                    Go to Login
                  </Button>
                </div>
              ) : isSuccess ? (
                <div className="text-center space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Redirecting you to the login page...
                  </p>
                  <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {tokenEmail && (
                    <p className="text-sm text-muted-foreground text-center mb-4">
                      Resetting password for: <strong>{tokenEmail}</strong>
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                      className="h-11"
                      placeholder="Enter new password"
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                      className="h-11"
                      placeholder="Confirm new password"
                      minLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="p-4 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Daniel Albert Law Firm. All rights reserved.
          </p>
        </div>
      </div>
    </>
  );
};

export default AdminResetPassword;
